import { Checkbox, Col, Form, Input, Row, Select } from 'antd';
import * as React from 'react';
import ReactCountryFlag from 'react-country-flag';

import { useTranslate } from '@sorpetaler/ui/src/utils/translation/useTranslation';
import { Button, Link } from '@sorpetaler/ui/src/components/Clickable';
import { IWidgetFormValues } from '@sorpetaler/ui/src/components/widgetForm';
import * as styles from '../DoorWidget.module.less';

interface IDoorFormProps {
  step1Selection: string;
  step2Selections: string[];
  onFormSubmit: (values: IWidgetFormValues) => void;
  isSubmitting?: boolean;
  submitError?: string;
}

export const DoorForm: React.FunctionComponent<IDoorFormProps> = ({
  step1Selection,
  step2Selections,
  onFormSubmit,
  isSubmitting = false,
  submitError,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<IWidgetFormValues & { phoneCountry?: string }>();
  // Time trap: set on mount (client-only) so the server can reject
  // impossibly fast bot submissions.
  const startedAt = React.useRef(0);
  React.useEffect(() => {
    startedAt.current = Date.now();
  }, []);

  const phoneOptions = React.useMemo(
    () => [
      { value: 'DE', label: 'Deutschland', code: ' +49' },
      { value: 'AT', label: 'Österreich', code: ' +43' },
      { value: 'CH', label: 'Schweiz', code: ' +41' },
    ],
    []
  );

  const [selectedPhoneCountry, setSelectedPhoneCountry] = React.useState(
    phoneOptions[0]
  );

  const selectionItems = [
    { label: t('doorType'), value: step1Selection || '—' },
    {
      label: t('design'),
      value: step2Selections.length ? step2Selections.join(', ') : '—',
    },
  ];

  const handleFinish = (
    values: IWidgetFormValues & { phoneCountry?: string }
  ) => {
    onFormSubmit({ ...values, _ts: startedAt.current });
  };

  const handlePhoneCountryChange = (value: string) => {
    const option =
      phoneOptions.find((item) => item.value === value) ?? phoneOptions[0];
    setSelectedPhoneCountry(option);
    form.setFieldsValue({ phoneCountry: option.value });
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.headlineRow}>
        <div className={styles.containerWrapper}>
          <h2 className={styles.headline}>{t('contactFormTitle')}</h2>
        </div>
      </div>

      <div className={styles.formContent}>
        <div className={styles.containerWrapper}>
          <div className={styles.selectionSection}>
            <span className={styles.selectionTitle}>{t('yourSelection')}</span>
            <ul className={styles.selectionList}>
              {selectionItems.map((item, index) => (
                <li key={item.label} className={styles.selectionListItem}>
                  <span className={styles.selectionIndex}>{index + 1}</span>
                  <div className={styles.selectionCopy}>
                    <span className={styles.selectionLabel}>{item.label}</span>
                    <span className={styles.selectionValue}>{item.value}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.formSection}>
            <div className={styles.formSectionHeader}>
              <h3>{t('whoReceivesOffer')}</h3>
              <span className={styles.requiredHint}>
                *{t('requiredFieldsReminder')}
              </span>
            </div>

            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              className={styles.contactForm}
              onFinish={handleFinish}
              initialValues={{ phoneCountry: phoneOptions[0].value }}
            >
              <Form.Item name="phoneCountry" hidden>
                <input type="hidden" />
              </Form.Item>

              {/* Honeypot: invisible to humans, bots auto-fill it and get dropped server-side */}
              <Form.Item name="fax" className={styles.honeypot}>
                <Input
                  name="fax"
                  tabIndex={-1}
                  autoComplete="one-time-code"
                  aria-hidden="true"
                />
              </Form.Item>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="Vorname"
                    rules={[
                      { required: true, message: t('firstNameMessage') || '' },
                    ]}
                  >
                    <Input
                      placeholder={`${t('firstname')}*`}
                      disabled={isSubmitting}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="Nachname"
                    rules={[
                      { required: true, message: t('lastNameMessage') || '' },
                    ]}
                  >
                    <Input
                      placeholder={`${t('lastname')}*`}
                      disabled={isSubmitting}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="Telefon"
                    rules={[{ pattern: /^\d+$/u, message: t('numberMessage') }]}
                  >
                    <div className={styles.phoneInputWrapper}>
                      <Select
                        value={selectedPhoneCountry.value}
                        onChange={handlePhoneCountryChange}
                        bordered={false}
                        className={styles.phoneSelectInside}
                        dropdownClassName={styles.phoneSelectDropdown}
                        aria-label={`Select country, current: ${selectedPhoneCountry.label}`}
                        disabled={isSubmitting}
                      >
                        {phoneOptions.map((option) => (
                          <Select.Option
                            key={option.value}
                            value={option.value}
                          >
                            <div className={styles.phoneSelectOption}>
                              <ReactCountryFlag
                                countryCode={option.value}
                                svg
                                style={{ width: '24px', height: '16px' }}
                                title={option.label}
                              />
                              <span className={styles.phoneCountryCode}>
                                {option.code}
                              </span>
                            </div>
                            <span className={styles.srOnly}>
                              {option.label}
                            </span>
                          </Select.Option>
                        ))}
                      </Select>
                      <Input
                        style={{ flex: 1 }}
                        placeholder={t('phone')}
                        disabled={isSubmitting}
                      />
                    </div>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="E-Mail"
                    rules={[
                      { required: true, message: t('emptyEmailMessage') },
                      { type: 'email', message: t('validEmailMessage') },
                    ]}
                  >
                    <Input
                      placeholder={`${t('emailAddress')}*`}
                      disabled={isSubmitting}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <h3 className={styles.formSubTitle}>
                {t('projectLocationQuestion')}
              </h3>

              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="PLZ"
                    rules={[
                      { required: true, message: t('postCodeMessage1') },
                      {
                        pattern: /^\d{4,5}$/u,
                        message:
                          t('postCodeMessage1') ||
                          'Postcode must be 4-5 digits',
                      },
                    ]}
                  >
                    <Input
                      placeholder={`${t('postCode')}*`}
                      inputMode="numeric"
                      maxLength={5}
                      disabled={isSubmitting}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="Ort"
                    rules={[{ required: true, message: t('locationMessage') }]}
                  >
                    <Input
                      placeholder={`${t('location')}*`}
                      disabled={isSubmitting}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="accept-check"
                valuePropName="checked"
                rules={[{ required: true, message: t('privacyPolicyMessage') }]}
              >
                <Checkbox
                  className={styles.privacyCheckbox}
                  disabled={isSubmitting}
                >
                  {t('acceptPrivarcyPolicyMsg1')}{' '}
                  <Link
                    href="/datenschutzerklaerung/"
                    className={styles.privacyLink}
                  >
                    {t('acceptPrivarcyPolicyMsg2')}
                  </Link>{' '}
                  {t('acceptPrivarcyPolicyMsg3')}
                </Checkbox>
              </Form.Item>

              {submitError && (
                <div className={styles.errorMessage}>{submitError}</div>
              )}

              <Form.Item shouldUpdate className={styles.submitRow}>
                {() => {
                  const privacyAccepted = form.getFieldValue('accept-check');
                  return (
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!privacyAccepted || isSubmitting}
                    >
                      {isSubmitting ? t('sending') : t('requestGuidePrices')}
                    </Button>
                  );
                }}
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};
